import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, UploadIcon, StarIcon, MoreHorizontalIcon } from 'lucide-react';
interface RoomImage {
  id: string;
  url: string;
  caption: string;
  uploadedBy: string;
  uploadDate: string;
  isActive: boolean;
  isFeatured?: boolean;
}
interface RoomAssetsProps {
  roomId: string;
  roomName: string;
  images: RoomImage[];
  onBack: () => void;
  onUpload: (files: FileList) => void;
  onUpdateImage: (imageId: string, updates: Partial<RoomImage>) => void;
  onDeleteImage: (imageId: string) => void;
}
export function RoomAssets({
  roomId,
  roomName,
  images,
  onBack,
  onUpload,
  onUpdateImage,
  onDeleteImage
}: RoomAssetsProps) {
  const {
    t
  } = useTranslation('rooms');
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('media');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedImages(images.map(img => img.id));
    } else {
      setSelectedImages([]);
    }
  };
  const handleSelectImage = (imageId: string, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    }
  };
  return <div className="space-y-6" data-id="element-10018" data-mp="h6nw331jtex9">
      <div className="flex items-center" data-id="element-10019" data-mp="m34gbm5mn952">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-10020" data-mp="m4632nw09k2m">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-10021" data-mp="r53k2x5a482j" />
          <span data-id="element-10022" data-mp="v5dt6iodbelm">{t('components.RoomAssets.room_assets')}</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-10023" data-mp="102dwwmxqw59k">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-10024" data-mp="151qil4cvr6ak">
          <nav className="flex" role="tablist" aria-label="Room form sections" data-id="element-10025" data-mp="150ffl6r9e2ki">
            <button role="tab" aria-selected={activeTab === 'info'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-10026" data-mp="19tqnzcbpyrej">
              {t('components.EditRoomForm.room_information')}
            </button>
            <button role="tab" aria-selected={activeTab === 'media'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('media')} data-id="element-10027" data-mp="388o6fecn3jpb">
              {t('components.EditRoomForm.media_assets')}
            </button>
          </nav>
        </div>
        {/* Media Assets Content */}
        <div className="p-6" data-id="element-10028" data-mp="3n21ct5zjhyk5">
          {/* File Upload Area */}
          <div className={`border-2 border-dashed rounded-lg p-8 mb-6 flex flex-col items-center justify-center ${dragActive ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop} data-id="element-10029" data-mp="3i7x52kig9mi0">
            <div className="p-3 bg-red-100 rounded-full mb-4" data-id="element-10030" data-mp="3pj9x7xclt9zt">
              <UploadIcon size={24} className="text-red-500" data-id="element-10031" data-mp="3phqi4a2op4zm" />
            </div>
            <p className="text-gray-600 mb-1" data-id="element-10032" data-mp="3ujfqfhyiv4k0">
              {t('components.RoomAssets.drag_files_here')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-10033" data-mp="3gkg4ii2e93rj">
              {t('components.RoomAssets.supported_formats_png_jpg_jpeg')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-10034" data-mp="d251m6x0yvot">
              {t('components.RoomAssets.max_file_size_10mb')}
            </p>
            <label className="cursor-pointer" data-id="element-10035" data-mp="h26qffhnvumo">
              <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleFileChange} multiple data-id="element-10036" data-mp="h5b5x90kucsn" />

              <span className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700" data-id="element-10037" data-mp="w07h9v3ong9y">
                {t('components.RoomAssets.upload')}
              </span>
            </label>
          </div>
          {/* Images Table */}
          <div className="overflow-x-auto" data-id="element-10038" data-mp="1a4yawnb74s36">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-10039" data-mp="164nv6ixlqnb4">
              <thead data-id="element-10040" data-mp="164fkmxoybu33">
                <tr data-id="element-10041" data-mp="162uxht4yvdf9">
                  <th className="w-12 py-3 pl-4" data-id="element-10042" data-mp="1jwlyue8jnqzw">
                    <input type="checkbox" checked={selectedImages.length === images.length && images.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-10043" data-mp="1jvavgfj0gl9b" />
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10044" data-mp="116w6ryw61jbo">
                    {t('components.RoomAssets.room_image')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10045" data-mp="2cj6sjlchsf6m">
                    {t('components.RoomAssets.caption')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10046" data-mp="2fll1bfqvaoi6">
                    {t('components.RoomAssets.uploaded_by')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10047" data-mp="2oocdxivhbqbe">
                    {t('components.RoomAssets.uploaded')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10048" data-mp="1vzetpa7pjjg0">
                    {t('components.RoomAssets.visibility')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10049" data-mp="25w9pvmgid7r4">
                    {t('components.RoomAssets.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-10050" data-mp="1d7cjohpkn1eq">
                {images.map(image => <tr key={image.id} data-id="element-10051" data-mp="1i4vzhm1x3m8s">
                    <td className="py-4 pl-4" data-id="element-10052" data-mp="1anzhewl27a9g">
                      <input type="checkbox" checked={selectedImages.includes(image.id)} onChange={e => handleSelectImage(image.id, e.target.checked)} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-10053" data-mp="1amoeeyzfu6je" />
                    </td>
                    <td className="py-4" data-id="element-10054" data-mp="rpwnc3dnmn4s">
                      <div className="w-24 h-16 bg-gray-100 rounded overflow-hidden" data-id="element-10055" data-mp="mxbvh7eb6k3w">
                        <img src={image.url} alt={image.caption || 'Room image'} className="w-full h-full object-cover" data-id="element-10056" data-mp="hysb3bfd5gdn" />
                      </div>
                    </td>
                    <td className="py-4" data-id="element-10057" data-mp="82p5twp7t1mh">
                      <div className="flex items-center" data-id="element-10058" data-mp="3yhj252bhvqw">
                        {image.isFeatured && <StarIcon size={16} className="text-yellow-400 mr-1" data-id="element-10059" data-mp="1rvfko4s1m6qc" />}
                        <span data-id="element-10060" data-mp="1hy14m6a17jyk">{image.caption || 'Untitled'}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-10061" data-mp="1mvkuy4k07n6v">
                      {image.uploadedBy}
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-10062" data-mp="193wjm3hkx2qy">
                      {image.uploadDate}
                    </td>
                    <td className="py-4" data-id="element-10063" data-mp="z71nfr8s3efu">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${image.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10064" data-mp="z8cohjaua839">
                        {image.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4" data-id="element-10065" data-mp="ua1d344wbpix">
                      <button aria-label="Image actions" className="text-gray-400 hover:text-gray-500" data-id="element-10066" data-mp="bj4srg7b4gjl">
                        <MoreHorizontalIcon size={16} aria-hidden="true" data-id="element-10067" data-mp="kkbattpltzey" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}